import React, { FC, useEffect, useLayoutEffect } from 'react';
import classnames from 'classnames';

import { useProgressWidthInPercentages } from './hooks/use-progress-width';
import { useTimelineDrag } from './hooks/use-timeline-drag';
import styles from './styles.module.css';

interface Props {
    className?: string;
    containerLeftPadding?: number;
    timelineWidth?: number;
    isTimelineHovered?: boolean;

    onDragStateChanged: (isDragging: boolean) => void;
}

export const ProgressBar: FC<Props> = ({
    className,
    containerLeftPadding = 0,
    timelineWidth = 0,
    isTimelineHovered,
    onDragStateChanged,
}) => {
    const progressWidth = useProgressWidthInPercentages();
    const { dragPositionOnTimeline, isDragged, onDragStart } = useTimelineDrag({
        timelineWidth,
        startPosition: containerLeftPadding,
    });

    useEffect(() => {
        onDragStateChanged(isDragged);
    }, [isDragged, onDragStateChanged]);

    useLayoutEffect(() => {
        if (isDragged) {
            document.body.classList.add(styles.preventHover);
            document.documentElement.classList.add(styles.cursorPointer);
            return () => {
                document.body.classList.remove(styles.preventHover);
                document.documentElement.classList.remove(styles.cursorPointer);
            };
        }
    }, [isDragged]);

    return (
        <div>
            <div
                className={classnames(styles.root, className)}
                style={{
                    transform: `scaleX(${
                        isDragged && dragPositionOnTimeline
                            ? (dragPositionOnTimeline - containerLeftPadding) / timelineWidth
                            : progressWidth
                    })`,
                }}
            />
            <button
                aria-hidden
                onMouseDown={onDragStart}
                className={classnames(styles.pin, {
                    [styles.active]: isDragged || isTimelineHovered,
                })}
                style={{
                    left:
                        isDragged && dragPositionOnTimeline
                            ? dragPositionOnTimeline - containerLeftPadding
                            : `${progressWidth * 100}%`,
                }}
            />
        </div>
    );
};
